/* eslint @typescript-eslint/no-explicit-any: 0 */
import { HttpClient } from '@angular/common/http';
import { Injector } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { GroupHeaderTitle, ListResponse } from '@ca/core/models';
import { TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FeatureTogglesService } from 'src/app/core/services/feature-toggles.service';
import { InputOutputType } from '../app/core/enums/input-output-type.enum';
import { GroupedRowData } from '../app/core/models/grouped-row-data.model';
import { ModuleSpecification, SpecProperty, UIProperty } from '../app/core/models/module-specification.model';
import { SpecificationPropertyObject } from '../app/core/models/specification-property-object.model';
import { AuthenticationService } from '../app/core/services/authentication.service';
import { UserService } from '../app/core/services/user.service';

export interface groupedRowDataOptions {
    data: any[];
    groupByProperty: string;
    headerTitleProperty?: string;
    headerTitleFn?: (groupKey: string, data: any) => string;
    shouldBeCollapsedByDefault?: boolean;
    groupSortCompareFn?: (a: any, b: any) => number;
}

export function getGroupedRowData(groupedRowDataOptions: groupedRowDataOptions): GroupedRowData<GroupHeaderTitle, any>[] {
    let shouldBeCollapsedByDefault = false;
    if (groupedRowDataOptions.shouldBeCollapsedByDefault != null) {
        shouldBeCollapsedByDefault = groupedRowDataOptions.shouldBeCollapsedByDefault;
    }
    let groupedRowData: GroupedRowData<any, any>[] = [];
    const alreadyGroupedItems: string[] = [];

    groupedRowDataOptions.data.map((item: string) => {
        // Only consider item if it hasn't been grouped yet
        if (item[groupedRowDataOptions.groupByProperty] != null && alreadyGroupedItems.indexOf(item[groupedRowDataOptions.groupByProperty]) === -1) {
            let title = '';
            if (typeof groupedRowDataOptions.headerTitleFn === 'function') {
                title = groupedRowDataOptions.headerTitleFn(item[groupedRowDataOptions.groupByProperty], groupedRowDataOptions.data);
            } else if (groupedRowDataOptions.headerTitleProperty != null) {
                title = groupedRowDataOptions.headerTitleProperty ? item[groupedRowDataOptions.headerTitleProperty] || '' : '';
            }

            const elem: GroupedRowData<GroupHeaderTitle, any> = {
                id: item[groupedRowDataOptions.groupByProperty],
                header: { title },
                children: groupedRowDataOptions.data.filter(
                    (obj: any) => obj[groupedRowDataOptions.groupByProperty] === item[groupedRowDataOptions.groupByProperty],
                ),
                expanded: !shouldBeCollapsedByDefault,
                selected: false,
                touched: false,
            };

            groupedRowData.push(elem);
            alreadyGroupedItems.push(item[groupedRowDataOptions.groupByProperty]);
        }
    });

    if (typeof groupedRowDataOptions.groupSortCompareFn === 'function') {
        groupedRowData = groupedRowData.map((g: GroupedRowData<any, any>) => ({
            ...g,
            children: g.children.sort(groupedRowDataOptions.groupSortCompareFn),
        }));
    }

    return groupedRowData;
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/languages/', '.json');
}

/**
 * Creates a specification property object, which bundles all useful information from a property (from the module specification) into one object.
 */
export function getSpecificationPropertyObject(propKey: string, specification: ModuleSpecification): SpecificationPropertyObject | undefined {
    // Find the spec prop
    let specProp: SpecProperty<any> | undefined = undefined;
    let uiProp: UIProperty | undefined = undefined;
    const propType: InputOutputType | undefined = getSpecificationPropertyType(propKey, specification);

    if (specification != null && propType != null) {
        // get spec property
        if (propType === InputOutputType.Input && specification.spec.inputs[propKey!] != null) {
            specProp = specification.spec.inputs[propKey!];
        } else if (propType === InputOutputType.Output && specification.spec.outputs[propKey!] != null) {
            specProp = specification.spec.outputs[propKey!];
        }

        // get ui property
        // 346791: Inputs AND Outputs can have a UI part. Also Outputs need it (for displaying a label and description).
        // If a UI part doesn't exist, the key is taken as label.
        if (propType === InputOutputType.Input && specification.ui != null && specification.ui.inputs[propKey!] != null) {
            uiProp = specification.ui.inputs[propKey!];
        } else if (propType === InputOutputType.Output && specification.ui != null && specification.ui.outputs && specification.ui.outputs[propKey!] != null) {
            uiProp = specification.ui.outputs[propKey!];
        }
    }
    if (propType != null && specProp != null) {
        return new SpecificationPropertyObject(propKey, propType, specProp, uiProp);
    }
}

function getSpecificationPropertyType(propKey: string, specification: ModuleSpecification): InputOutputType | undefined {
    let type: InputOutputType | undefined = undefined;

    if (specification.spec != null) {
        // Check in inputs
        for (const key of Object.keys(specification.spec.inputs)) {
            if (propKey === key && specification.spec.inputs[key] != null) {
                type = InputOutputType.Input;
                break;
            }
        }

        // Check in outputs
        if (specification.spec.outputs != null) {
            for (const key of Object.keys(specification.spec.outputs)) {
                if (propKey === key && specification.spec.outputs[key] != null) {
                    type = InputOutputType.Output;
                    break;
                }
            }
        }
    }

    return type;
}
export function initialUserInfoRefresh(injector: Injector): () => Promise<unknown> {
    return (): Promise<unknown> => {
        return injector
            .get(AuthenticationService)
            .configureAuth()
            .then(() => injector.get(UserService).refreshUserInfo$().toPromise())
            .then(() => injector.get(UserService).loadUserSubscription())
            .then(() => injector.get(FeatureTogglesService).getUserFeatureToggles$().toPromise);
    };
}

export function updateDefaultApeerMetaTags(meta: Meta, translate: TranslateService): void {
    meta.updateTag({ property: 'og:title', content: translate.instant('META_TAGS.TITLE') });
    meta.updateTag({ property: 'og:description', content: translate.instant('META_TAGS.SOCIAL_DESCRIPTION') });
    meta.updateTag({ property: 'og:url', content: translate.instant('META_TAGS.URL') });
    meta.updateTag({ property: 'og:image', content: translate.instant('META_TAGS.IMAGE_URL') });
}

export function parseBool(boolString: string): boolean {
    return boolString === 'true';
}

/**
 * a - first object
 * b - second object
 * clone - this method removes elements and changes original object, so we need to clone original objects only if they are not already cloned
 * key - name of property we need to send for recursive call
 * fieldsToRespectOrder - list of array fields names for which order is important
 */
export function deepEqualInAnyOrder(a: object, b: object, clone: boolean = true, key: string | undefined, fieldsToRespectOrder: string[]): boolean {
    // this method removes elements and changes original object, so we need to clone original objects
    // if objects are already cloned there is no need to clone them
    if (clone) {
        a = JSON.parse(JSON.stringify(a));
        b = JSON.parse(JSON.stringify(b));
    }
    if ((a && !b) || (!a && b)) {
        return false;
    }
    if (!a && !b) {
        return true;
    }
    if (typeof a !== typeof b || (Array.isArray(a) && !Array.isArray(b)) || (!Array.isArray(a) && Array.isArray(b))) {
        return false;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }

        if (key && fieldsToRespectOrder.indexOf(key) > -1) {
            for (let i = 0; i < a.length; i++) {
                if (deepEqualInAnyOrder(a[i], b[i], false, undefined, fieldsToRespectOrder)) {
                    a.splice(i, 1);
                    b.splice(i, 1);
                    i--;
                }
            }
        } else {
            for (let i = 0; i < a.length; i++) {
                for (let j = 0; j < b.length; j++) {
                    if (deepEqualInAnyOrder(a[i], b[j], false, undefined, fieldsToRespectOrder)) {
                        a.splice(i, 1);
                        i--;
                        b.splice(j, 1);
                        j--;
                    }
                }
            }
        }

        if (a.length === 0 && b.length === 0) {
            return true;
        }

        return false;
    }

    if (typeof a === 'object' && typeof b === 'object') {
        const aKeys: string[] = Object.keys(a);
        const bKeys: string[] = Object.keys(b);
        if (aKeys.length !== bKeys.length) {
            return false;
        }
        for (let i = 0; i < aKeys.length; i++) {
            for (let j = 0; j < bKeys.length; j++) {
                if (aKeys[i] === bKeys[j] && deepEqualInAnyOrder(a[aKeys[i]], b[bKeys[j]], false, aKeys[i], fieldsToRespectOrder)) {
                    aKeys.splice(i, 1);
                    i--;
                    bKeys.splice(j, 1);
                    j--;
                }
            }
        }

        if (aKeys.length === 0 && bKeys.length === 0) {
            return true;
        }
        return false;
    }

    return a === b;
}

/**
 * data - data to stringify
 * opts - options
 */
export function stableStringify(data: object, opts?: any) {
    if (!opts) {
        opts = {};
    }
    if (typeof opts === 'function') {
        opts = { cmp: opts };
    }
    const cycles = typeof opts.cycles === 'boolean' ? opts.cycles : false;
    // properties to skip from stringify - will be removed from string
    const skip = opts.skip;

    const cmp =
        opts.cmp &&
        (function (f) {
            return function (node) {
                return function (a, b) {
                    const aobj = { key: a, value: node[a] };
                    const bobj = { key: b, value: node[b] };
                    return f(aobj, bobj);
                };
            };
        })(opts.cmp);

    const seen: Array<any> = [];
    return (function stringify(node: any) {
        if (node && node.toJSON && typeof node.toJSON === 'function') {
            node = node.toJSON();
        }

        if (node === undefined) {
            return 'null';
        }
        if (typeof node === 'number') {
            return isFinite(node) ? '' + node : 'null';
        }
        if (typeof node !== 'object') {
            return JSON.stringify(node);
        }

        let i: number;
        let out: string;
        if (Array.isArray(node)) {
            out = '[';
            for (i = 0; i < node.length; i++) {
                if (i) {
                    out += ',';
                }
                out += stringify(node[i]) || 'null';
            }
            return out + ']';
        }

        if (node === null) {
            return 'null';
        }

        if (seen.indexOf(node) !== -1) {
            if (cycles) {
                return JSON.stringify('__cycle__');
            }
            throw new TypeError('Converting circular structure to JSON');
        }

        const seenIndex = seen.push(node) - 1;
        const keys = Object.keys(node).sort(cmp && cmp(node));
        out = '';
        for (i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = stringify(node[key]);

            if (out && (!skip || skip.indexOf(key) === -1)) {
                out += ',';
            }
            if (!skip || skip.indexOf(key) === -1) {
                out += JSON.stringify(key) + ':' + value;
            }
        }
        seen.splice(seenIndex, 1);
        return '{' + out + '}';
    })(data);
}

export function trimSlash(input: string): string {
    if (input[input.length - 1] === '/') {
        return trimSlash(input.slice(0, input.length - 1));
    }
    return input;
}

// use windows drive format, e.g. '/c/mount' becomes 'c:/mount'
export function adjustWindowsFormat(path: string): string {
    if (!path || path.length < 3 || !window.navigator.appVersion.includes('Win')) {
        return path;
    }

    if (path[0] === '/' && path[2] === '/') {
        path = path[1] + ':' + path.substr(2);
    }
    return path;
}

export function sortByValue(propName: string, direction: 'asc' | 'desc' = 'asc') {
    return function (a, b) {
        const valA = a[propName].toLowerCase();
        const valB = b[propName].toLowerCase();
        if (valA < valB) {
            return direction === 'asc' ? -1 : 1;
        }
        if (valA > valB) {
            return direction === 'asc' ? 1 : -1;
        }

        return 0;
    };
}

export function createListResponseWithTotalCount<T>(data: T[], totalRecordCount: number): ListResponse<T> {
    return { data: data, meta: { totalCount: totalRecordCount } };
}

export function isOldWorkflowStarter(title: string, index: number) {
    return title === 'Single File Upload' && index === 0;
}

export function isOldSingleFileUpload(title: string, index: number) {
    return title === 'Single File Upload' && index !== 0;
}

// @ts-ignore
export function replacer(key, value) {
    if (value instanceof Map) {
        return {
            dataType: 'Map',
            value: Array.from(value.entries()),
        };
    } else {
        return value;
    }
}

// @ts-ignore
export function reviver(key, value) {
    if (typeof value === 'object' && value !== null) {
        if (value.dataType === 'Map') {
            return new Map(value.value);
        }
    }
    return value;
}

export function isEqual(object1?: object | string[], object2?: object | string[]): boolean {
    if (object1 === object2) {
        return true;
    }

    if (object1 == null || object2 == null || typeof object1 != 'object' || typeof object2 != 'object') {
        return false;
    }
    const keys1: string[] = Object.keys(object1);
    const keys2: string[] = Object.keys(object2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key) || !isEqual(object1[key], object2[key])) {
            return false;
        }
    }

    return true;
}
